/**
 * authorizeCardHtml.ts — the PURE html for every held-action card, in one place.
 *
 * WHY IT IS ITS OWN MODULE (P4, 2026-09-16). The card is DATA; its html is a
 * projection (contract C2). A governance card posted into an approver's own
 * thread is rebuilt from its persisted payload at every hydrate, and that
 * rebuild runs INSIDE the chat reducer (connectTileRehydrate → useGlobalChat),
 * so the builder it calls may not drag `firebase/auth` into the reducer's
 * import graph — `src/services/prest0nSession/client.ts` imports it and touches
 * global fetch at import time (the load-time trap CLAUDE.md names). Everything
 * here imports the pure card kit and TYPES ONLY, so the markup has exactly one
 * owner and the reducer can call it.
 *
 * TWO KINDS, ONE FAMILY:
 *   - a held SEND (2026-08-18): recipient, subject, the full draft, send /
 *     don't-send. Byte-identical to what shipped — these bodies were moved
 *     here unchanged.
 *   - a held TEAM OR ROLE CHANGE (P4): the change in one line, the facts
 *     beneath it, and the decision. Three views, because three different
 *     people can be looking at the same hold:
 *       self      — the asker may make the change; the ladder asks them to
 *                   confirm once (Miles, 2026-09-15: card for roles, first
 *                   invite only).
 *       approver  — the asker may NOT; this is a colleague who can, reading it
 *                   in a thread of their own. Their tap makes the change under
 *                   THEIR authority.
 *       waiting   — the asker's own card while an approver decides. It offers
 *                   only "withdraw": the asker can never approve their own ask,
 *                   and the server refuses it even if this html lied.
 *
 * D9: every string here is state, refusal or failure copy — the facts of what
 * was asked and what happened, never content.
 */

import type {
  PendingActionOutcome,
  PendingActionPreview,
} from '../../prest0nSession/client';
import {
  P0AC,
  escapeHtml,
  renderCardShell,
  renderStateIcon,
  renderStatusChip,
  renderPillButton,
  renderMetaRow,
  renderRecipientLine,
  splitRecipients,
  renderAttachmentLine,
  renderAttachmentIssueLine,
  renderDetailBlock,
  renderBodyBlock,
  renderFooterRow,
  type CardAttachment,
  type CardAttachmentIssue,
} from './actionCardKit';
import { failureNeedsExplainTurn } from './sendOutcomePolicy';

/** Button id prefixes for the click handler (per-action suffix keeps the
 * double-click guard scoped to one card). */
export const AUTHORIZE_ACTION_BUTTON_IDS = {
  approve: 'authorize-action-approve',
  cancel: 'authorize-action-cancel',
  /** On a receipt whose failure left the draft armed — see `retryable`. */
  retry: 'authorize-action-retry',
} as const;

// Visuals come from actionCardKit — one design system across every send
// surface (owner decision 2026-08-27). CONFIRMATION IS PREST0 GOLD, NOT
// GREEN (owner directive 2026-08-25, ask 6): P0AC.OK_INK is the house gold.
const SUBTLE = P0AC.SUBTLE;
const OK_INK = P0AC.OK_INK;
const WARN_INK = P0AC.WARN_INK;

export type StagedSendRowState =
  | 'pending' | 'sending' | 'sent' | 'auto-sent' | 'skipped'
  | 'failed' | 'unconfirmed' | 'superseded' | 'expired';

/**
 * One staged send, as the card shows it: WHO it goes out from and to, what it
 * is called, what rides with it, its FULL draft body, and its decision state.
 * ONE TILE (owner directive 2026-08-29): the card is the whole message — the
 * complete draft renders as flowing prose on its row, so the user reads
 * exactly what each send will say right where they approve it.
 *
 * `to`/`cc`/`bcc` are comma-separated strings, the shape the tool schema and
 * the provider both speak; `renderRecipientLine` is the ONE parse that turns
 * them into what the reader sees.
 */
export interface StagedSendRow {
  actionId: string;
  batchIndex: number;
  to: string;
  cc: string;
  bcc?: string;
  subject: string;
  from: string | null;
  /** The FULL draft body, exactly as it will send (server caps at 20k). */
  body: string;
  /** True only when the server hit its display cap — the send is unaffected. */
  bodyTruncated?: boolean;
  /** True when the server clipped the recipient list for storage. */
  recipientsTruncated?: boolean;
  /** Files riding with this send — locators only, never bytes. */
  attachments?: CardAttachment[];
  /** Files that were named and are NOT going out, with why. */
  attachmentIssues?: CardAttachmentIssue[];
  /** Who last changed this draft by hand. Its PRESENCE also locks the row
   *  against Prest0n's supersede sweep, server-side (2026-09-17). */
  lastEditedBy?: { uid: string; name: string; atMs: number };
  state: StagedSendRowState;
  /** Failure detail for the row's receipt line (class copy, never internals). */
  note: string | null;
  retryable: boolean;
}

// State-line inks — the kit palette (warm ink + gold; gold, never green).
function stagedStateLine(row: StagedSendRow): { label: string; ink: string } {
  switch (row.state) {
    case 'sent': return { label: 'sent — confirmed by your email provider', ink: OK_INK };
    case 'auto-sent': return { label: 'auto sent — confirmed by your email provider', ink: OK_INK };
    case 'sending': return { label: 'sending…', ink: SUBTLE };
    case 'skipped': return { label: 'skipped — you chose not to send this one', ink: SUBTLE };
    case 'superseded': return { label: 'replaced by a newer draft', ink: SUBTLE };
    case 'expired': return { label: 'expired before it was sent', ink: WARN_INK };
    case 'unconfirmed': return { label: 'not confirmed — we cannot say it went out', ink: WARN_INK };
    // Only the NON-retryable failure lands here — a retryable one keeps its
    // buttons (`actionable`) and says why on its note line.
    case 'failed': return { label: `not sent${row.note ? ` — ${row.note}` : ''}`, ink: WARN_INK };
    default: return { label: '', ink: SUBTLE };
  }
}

/** State-line icon: rides the state span, never a decision button. */
function stagedStateIcon(state: StagedSendRowState): string {
  switch (state) {
    case 'sent': case 'auto-sent': return renderStateIcon('check');
    case 'sending': return renderStateIcon('arrow');
    case 'expired': return renderStateIcon('clock');
    case 'unconfirmed': case 'failed': return renderStateIcon('alert');
    default: return '';
  }
}

/**
 * Below this many rows the stack renders at its natural height. At or above
 * it the list gets its own scroll so the card cannot swallow the transcript.
 *
 * The scroll box is scoped to the multi-row LIST and nothing else, which is
 * what keeps it on the right side of the no-preview-windows directive
 * (2026-08-29): that rule bans a cramped box showing a TRUNCATED FRAGMENT of
 * one body. Nothing is truncated here and no single email is ever boxed — at
 * one row there is no scroll container at all. Owner's choice, 2026-09-17:
 * "scrollable stack, all expanded".
 */
export const STAGED_SEND_SCROLL_THRESHOLD = 4;

/** A DOM safety ceiling, not a display policy: beyond this a pathological
 *  batch would mint tens of thousands of nodes. Rows past it are counted and
 *  named, never silently dropped. */
export const STAGED_SEND_ROW_CEILING = 200;

/** How many row ids the root's self-description carries. Deliberately HIGHER
 *  than the row ceiling: an id is ~20 bytes, and the attribute is the only
 *  place a row that was not drawn survives a page refresh — the same reason
 *  it has always listed every row, back when only 20 of them rendered. */
export const STAGED_SEND_ID_CEILING = 1_000;

/**
 * Total draft-body characters the card may display across its rows
 * (adversarial review 2026-08-29): 20 rows x 20k server-capped bodies would
 * put 400KB+ on ONE persisted message and blow the thread doc budget
 * (INCIDENT-20260822 class). The budget splits evenly across rows with a
 * floor; a clipped row shows an honest display-cap note — the SEND always
 * uses the verbatim held params.
 */
export const STAGED_SEND_BODY_BUDGET = 120_000;
/**
 * The per-row floor is DERIVED from the ceiling, never chosen: a fixed floor
 * of 2,000 was safe while only 20 rows rendered, but with every staged email
 * now drawn, 200 rows x 2,000 would persist ~400KB on ONE message. Deriving
 * it means rows x floor can never exceed the budget, whatever the row count —
 * the budget is fixed, which is what its name has always claimed.
 */
const STAGED_SEND_BODY_FLOOR = Math.floor(STAGED_SEND_BODY_BUDGET / STAGED_SEND_ROW_CEILING);

/** The addressing block every row carries, singular or not. */
function stagedAddressing(row: StagedSendRow, indent: string): string {
  const rows = [
    renderRecipientLine('to:', row.to),
    renderRecipientLine('cc:', row.cc),
    renderRecipientLine('bcc:', row.bcc),
    renderMetaRow('subject:', escapeHtml(row.subject || '')),
    renderAttachmentLine(row.attachments),
    renderAttachmentIssueLine(row.attachmentIssues),
    row.recipientsTruncated
      ? '<div class="p0ac-row-note">the recipient list was clipped for storage — check it before sending.</div>'
      : '',
  ].filter(Boolean);
  return rows.join(`\n${indent}`);
}

/**
 * THE staged-send card — one renderer for one email and for forty (owner
 * directive 2026-09-17: "if there is only one it shouldn't look like there
 * are multiple"). The count decides the chrome, never a separate builder:
 * at one row there is no index number, no counts, no send-all footer and no
 * scroll box; the card simply is that email.
 *
 * Pure and idempotent — every render rebuilds from the full row array and
 * never patches html (the anti-race rule). Lives in this Firebase-free leaf
 * so the chat reducer can re-project it from a payload.
 */
export function buildStagedSendCardHtml(opts: { batchId: string; rows: ReadonlyArray<StagedSendRow> }): string {
  const all = [...opts.rows].sort(
    (a, b) => (a.batchIndex - b.batchIndex) || a.actionId.localeCompare(b.actionId),
  );
  if (all.length === 0) return '';
  const rows = all.slice(0, STAGED_SEND_ROW_CEILING);
  const beyondCeiling = all.length - rows.length;
  const solo = rows.length === 1;
  const bid = escapeHtml(opts.batchId || '');

  const perRowBodyCap = Math.max(
    STAGED_SEND_BODY_FLOOR,
    Math.floor(STAGED_SEND_BODY_BUDGET / rows.length),
  );

  const buttonsFor = (row: StagedSendRow): string => {
    const id = escapeHtml(row.actionId);
    const to = escapeHtml(row.to || '');
    const subject = escapeHtml(row.subject || '');
    const dataAttrs = `data-action-id="${id}" data-to="${to}" data-subject="${subject}"`;
    const failedRetry = row.state === 'failed' && row.retryable;
    const approveId = `${AUTHORIZE_ACTION_BUTTON_IDS.approve}-${row.actionId}`;
    const cancelId = `${AUTHORIZE_ACTION_BUTTON_IDS.cancel}-${row.actionId}`;
    // A single email keeps the one-card shape: full-size pills below the
    // body, and "don't send" — the words for a decision about THIS email,
    // not a row in a list.
    if (solo) {
      return `<div class="p0ac-actions">
    ${renderPillButton({ id: approveId, label: failedRetry ? 'try again' : 'send it', variant: 'primary', size: 'md', dataAttrs })}
    ${renderPillButton({ id: cancelId, label: "don't send", variant: 'ghost', size: 'md', dataAttrs })}
  </div>`;
    }
    return `<div class="p0ac-btnrow">
        ${renderPillButton({ id: approveId, label: failedRetry ? 'try again' : 'send it', variant: 'primary', size: 'sm', dataAttrs })}
        ${renderPillButton({ id: cancelId, label: 'skip', variant: 'ghost', size: 'sm', dataAttrs })}
      </div>`;
  };

  // ---- one email: the card IS that email -----------------------------------
  if (solo) {
    const row = rows[0];
    const from = escapeHtml(row.from || '');
    const st = stagedStateLine(row);
    const actionable = row.state === 'pending' || (row.state === 'failed' && row.retryable);
    const noteLine = actionable && row.note
      ? `\n  <div class="p0ac-row-note">${escapeHtml(row.state === 'failed' ? `not sent — ${row.note}` : row.note)}</div>`
      : '';
    const control = actionable
      ? `<div class="p0ac-note">nothing goes out until you tap send it — and you will get a receipt here either way.</div>
  ${buttonsFor(row)}`
      : `<div class="p0ac-state" style="color:${st.ink};">${stagedStateIcon(row.state)}<span>${escapeHtml(st.label)}</span></div>`;
    const shown = row.body.slice(0, perRowBodyCap);
    const bodyBlock = shown
      ? `\n  ${renderBodyBlock(shown, row.bodyTruncated || shown.length < row.body.length)}`
      : '';
    return renderCardShell({
      rootAttrs: bid ? `data-batch-root="${bid}" data-batch-action-ids="${escapeHtml(row.actionId)}"` : undefined,
      maxWidth: 560,
      title: 'ready to send — your call',
      bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${from ? `${renderMetaRow('from:', from)}\n    ` : ''}${stagedAddressing(row, '    ')}
  </div>${bodyBlock}${noteLine}
  ${control}`,
    });
  }

  // ---- several: a stack, every one of them readable -------------------------
  // Counted over EVERY staged row, never the drawn slice: "send all
  // remaining" loops the whole registry, so a label counting only what is on
  // screen would understate what the button actually does.
  const sent = all.filter((r) => r.state === 'sent' || r.state === 'auto-sent').length;
  const awaiting = all.filter((r) => r.state === 'pending' || r.state === 'sending').length;
  const skipped = all.filter((r) => r.state === 'skipped').length;
  const other = all.length - sent - awaiting - skipped;

  // Header status chips — non-zero counts only (a "0 skipped" pill is noise).
  const chips = [
    renderStatusChip('neutral', `${all.length} staged`),
    sent > 0 ? renderStatusChip('ok', `${sent} sent`) : '',
    awaiting > 0 ? renderStatusChip('neutral', `${awaiting} awaiting your call`) : '',
    skipped > 0 ? renderStatusChip('muted', `${skipped} skipped`) : '',
    other > 0 ? renderStatusChip('warn', `${other} other`) : '',
  ].filter(Boolean).join('\n    ');

  // The jump rail — how "quickly view all emails suggested" is answered while
  // every body stays expanded. BUTTONS, never anchors: the delegated chat
  // handler dispatches only buttons, and an <a href="#…"> would be taken for
  // in-app navigation and then rewritten by ensureLinksOpenInNewTab.
  const jump = rows
    .map((r) => {
      const who = splitRecipientsForLabel(r.to);
      return renderPillButton({
        id: `${STAGED_SEND_JUMP_BUTTON_ID}-${r.actionId}`,
        label: `#${r.batchIndex || '?'} ${who}`,
        variant: 'ghost',
        size: 'sm',
        dataAttrs: `data-action-id="${escapeHtml(r.actionId)}"`,
      });
    })
    .join('\n    ');

  const renderRow = (row: StagedSendRow): string => {
    const from = escapeHtml(row.from || '');
    const failedRetry = row.state === 'failed' && row.retryable;
    const actionable = row.state === 'pending' || failedRetry;
    const st = stagedStateLine(row);
    const right = actionable
      ? buttonsFor(row)
      : `<div class="p0ac-state" style="color:${st.ink};">${stagedStateIcon(row.state)}<span>${escapeHtml(st.label)}</span></div>`;
    const noteLine = actionable && row.note
      ? `\n        <div class="p0ac-row-note">${escapeHtml(failedRetry ? `not sent — ${row.note}` : row.note)}</div>`
      : '';
    const shown = row.body.slice(0, perRowBodyCap);
    const bodyBlock = shown
      ? `\n    ${renderBodyBlock(shown, row.bodyTruncated || shown.length < row.body.length)}`
      : '';
    return `<div class="p0ac-row" id="p0ac-row-${escapeHtml(row.actionId)}">
    <div class="p0ac-row-head">
      <div class="p0ac-row-meta">
        <div class="p0ac-row-eyebrow">#${row.batchIndex || '?'}${from ? ` · from: ${from}` : ''}</div>
        ${stagedAddressing(row, '        ')}${noteLine}
      </div>
      ${right}
    </div>${bodyBlock}
  </div>`;
  };

  const inFlight = all.some((r) => r.state === 'sending');
  const sendAll = awaiting > 0
    ? `\n  ${renderFooterRow({
        buttonHtml: renderPillButton({
          id: `${STAGED_SEND_ALL_BUTTON_ID}-${opts.batchId}`,
          label: inFlight ? 'sending…' : `send all remaining (${awaiting})`,
          variant: 'primary',
          size: 'md',
          disabled: inFlight,
          dataAttrs: `data-batch-id="${bid}"`,
        }),
        helperText: 'each send gets its own receipt on its row.',
      })}`
    : '';

  const ceilingNote = beyondCeiling > 0
    ? `\n  <div class="p0ac-note">…and ${beyondCeiling} more staged (decide them in words — "send them all" works).</div>`
    : '';
  const scrollClass = rows.length >= STAGED_SEND_SCROLL_THRESHOLD ? ' p0ac-rows--scroll' : '';
  const allActionIds = all.slice(0, STAGED_SEND_ID_CEILING).map((r) => escapeHtml(r.actionId)).join(',');
  return renderCardShell({
    rootAttrs: `data-batch-root="${bid}" data-batch-action-ids="${allActionIds}"`,
    title: `ready to send — your call, ${all.length} staged`,
    chipsHtml: `<div class="p0ac-chips">${chips}</div>`,
    noteHtml: `<div class="p0ac-note">nothing goes out until you tap send it — per row, or all at once. you get a receipt on each row either way.</div>
  <div class="p0ac-jump">${jump}</div>`,
    bodyHtml: `<div class="p0ac-rows${scrollClass}">
    ${rows.map(renderRow).join('\n    ')}
  </div>${ceilingNote}${sendAll}`,
  });
}

/** Button id prefix for a jump-rail pill (scrolls to a row; no network). */
export const STAGED_SEND_JUMP_BUTTON_ID = 'batch-jump';
/** Button id prefix for the send-all footer control. */
export const STAGED_SEND_ALL_BUTTON_ID = 'batch-send-all';

/** The jump pill's short label for a row: the first recipient's local part,
 *  enough to tell rows apart at a glance without spilling the whole address. */
function splitRecipientsForLabel(to: string): string {
  const first = splitRecipients(to)[0] || '';
  if (!first) return '';
  const at = first.indexOf('@');
  return at > 0 ? first.slice(0, at) : first;
}

/** The authorize card for ONE held email — now a thin wrapper over the one
 *  staged-send renderer, so a single email and a batch of forty can never
 *  drift apart again. */
function buildEmailAuthorizeCardHtml(actionId: string, preview: PendingActionPreview): string {
  return buildStagedSendCardHtml({
    batchId: '',
    rows: [{
      actionId,
      batchIndex: 0,
      to: preview.to || '',
      cc: preview.cc || '',
      bcc: (preview as { bcc?: string }).bcc || '',
      subject: preview.subject || '',
      from: preview.from || null,
      body: preview.bodyPreview || '',
      bodyTruncated: preview.bodyTruncated,
      attachments: (preview as { attachments?: CardAttachment[] }).attachments,
      attachmentIssues: (preview as { attachmentIssues?: CardAttachmentIssue[] }).attachmentIssues,
      state: 'pending',
      note: null,
      retryable: false,
    }],
  });
}

/** Which visual state a receipt renders in. */
type ReceiptTone = 'sent' | 'not-sent' | 'unconfirmed';

function receiptTone(outcome: PendingActionOutcome): ReceiptTone {
  if (outcome.delivered) return 'sent';
  if (outcome.kind === 'unconfirmed' || outcome.kind === 'unreachable') return 'unconfirmed';
  return 'not-sent';
}

/** Short local clock label for the receipt line. */
function receiptClock(decidedAt?: string): string {
  const when = decidedAt ? new Date(decidedAt) : new Date();
  if (Number.isNaN(when.getTime())) return '';
  try {
    return when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * The receipt card. A record, not a control — with ONE exception.
 *
 * It stays buttonless for every DECIDED outcome (sent, cancelled, refused for
 * a reason the same payload cannot survive), because a decided card that
 * still looks clickable is what the receipt was invented to stop.
 *
 * The exception is a failure the server has certified `retryable`: nothing
 * left the building and the SAME held draft is still armed. That is not a
 * decided send, it is an interrupted one, and the honest control for it is a
 * retry. Without it the 2026-08-24 receipt promised "once fixed, the same
 * send will work — no new draft needed" and then offered the user no way to
 * do that, which is how Prest0n ended up promising it again in prose.
 */
function buildEmailSendReceiptCardHtml(outcome: PendingActionOutcome): string {
  const tone = receiptTone(outcome);
  const ink = tone === 'sent' ? OK_INK : WARN_INK;
  const title =
    tone === 'sent'
      // "auto sent" when the standing teach authorized it (owner directive
      // 2026-08-25, ask 5) — the mode is never invisible on the record.
      ? outcome.autoApproved
        ? 'auto sent — confirmed by your email provider'
        : 'sent — confirmed by your email provider'
      : outcome.kind === 'cancelled'
        ? 'not sent — you cancelled it'
        : outcome.kind === 'superseded'
          // A newer draft replaced this card — the user cancelled NOTHING
          // (owner feedback 2026-08-25, ask 2).
          ? 'not sent — a newer draft replaced this one'
        : outcome.kind === 'expired'
          ? 'not sent — this card expired before it was sent'
          : outcome.kind === 'not-allowed'
            ? 'not sent — your role cannot send from this workspace'
            : outcome.kind === 'unreachable'
              ? 'not confirmed — the click never reached us'
              // These two used to explain themselves through a raw server
              // string pasted into "why:". That string is ours, not the email
              // provider's, so it no longer reaches the card — which means the
              // TITLE has to carry the explanation instead of leaving a bare
              // "not sent" the user cannot act on.
              : outcome.kind === 'already-decided'
                ? 'not sent — this card was already answered'
              : outcome.kind === 'not-found'
                ? 'not sent — this card is no longer available'
              : outcome.kind === 'internal-error'
                // Name the owner of the problem in the title itself. The
                // 2026-08-24 receipt said a bare "not sent" over a raw
                // 'COMPOSIO_API_KEY environment variable not set', which read
                // as "your email is broken". It was ours.
                ? 'not sent — a problem on our side, not yours'
                : tone === 'unconfirmed'
                  ? 'not confirmed — we cannot say it went out'
                  : 'not sent';
  const clock = receiptClock(outcome.decidedAt);
  const to = escapeHtml(outcome.to || '');
  const subject = escapeHtml(outcome.subject || '');
  const reason = escapeHtml(outcome.reason || '');
  const remediation = escapeHtml(outcome.remediation || '');
  // The FROM line replaces the provider id (owner directive 2026-08-25,
  // ask 4): "provider ID doesn't mean anything to the user". The id still
  // rides the structured payload for the record; it just never renders.
  const from = escapeHtml(outcome.from || '');

  const rows: string[] = [];
  if (from) rows.push(renderMetaRow('from:', from));
  if (to) rows.push(renderMetaRow('to:', to));
  if (subject) rows.push(renderMetaRow('subject:', subject));
  if (clock) {
    rows.push(renderMetaRow(tone === 'sent' ? 'sent at:' : 'at:', escapeHtml(clock)));
  }
  if (tone === 'sent') rows.push(`<div style="color:${SUBTLE};">it is in your Sent folder now.</div>`);

  const detail: string[] = [];
  if (tone !== 'sent' && reason) detail.push(renderMetaRow('why:', reason));
  if (tone !== 'sent' && remediation) {
    detail.push(`<div style="margin-top:6px;">${renderMetaRow('next:', remediation)}</div>`);
  }

  const actionId = escapeHtml(outcome.actionId || '');
  const retryId = `${AUTHORIZE_ACTION_BUTTON_IDS.retry}-${outcome.actionId || ''}`;
  // The retry renders only when this receipt will be the LAST word in the
  // thread. workflowCtaLifecycle is latest-actionable-only: any newer message
  // greys every button on the card above it, so a retry printed under an
  // outcome that also spawns an explain turn would arrive already dead. A
  // button that greys out a second after it appears is worse than no button.
  // (The server-side re-arm is NOT limited this way — a hold stays armed for
  // every re-armable kind, so "send it again" in words still works.)
  const retryStaysLive = !failureNeedsExplainTurn(outcome.kind);
  const retry =
    outcome.retryable && actionId && retryStaysLive
      ? `\n  ${renderFooterRow({
          buttonHtml: renderPillButton({
            id: retryId,
            label: 'try again',
            variant: 'primary',
            size: 'md',
            dataAttrs: `data-action-id="${actionId}" data-to="${to}" data-subject="${subject}"`,
          }),
          helperText: 'same email, nothing re-written.',
        })}`
      : '';

  // Tone lives in the title ink + state icon. why:/next: are plain LINES,
  // not a boxed window (owner directive 2026-08-29 — no preview/detail
  // windows inside action tiles): they read as part of the receipt instead
  // of an inset console pane. Gold for confirmed, never green.
  const titleIcon = tone === 'sent'
    ? renderStateIcon('check')
    : tone === 'unconfirmed'
      ? renderStateIcon('clock')
      : renderStateIcon('alert');
  return renderCardShell({
    maxWidth: 560,
    title,
    titleInk: ink,
    titleIconSvg: titleIcon,
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>${detail.length > 0 ? `\n  ${renderDetailBlock(detail)}` : ''}${retry}`,
  });
}


// ============================================================================
// THE GOVERNANCE CARD (P4, 2026-09-16) — a held team or role change
// ============================================================================

/** Which of the three readers is looking at this hold. */
export type GovernanceCardView = 'self' | 'approver' | 'waiting';

/** PURE. The view a card renders in, from the hold's own facts. */
export function governanceCardView(
  preview: PendingActionPreview,
  explicit?: GovernanceCardView | null,
): GovernanceCardView {
  if (explicit === 'self' || explicit === 'approver' || explicit === 'waiting') return explicit;
  return preview.awaitingApproval === true ? 'waiting' : 'self';
}

/** PURE. Is this hold a team/role change rather than an email? */
export function isGovernancePreview(preview: PendingActionPreview | null | undefined): boolean {
  return !!preview && preview.kind === 'rbac';
}

const GOVERNANCE_TITLES: Record<GovernanceCardView, string> = {
  self: 'confirm this change — your call',
  approver: 'a teammate needs your approval',
  waiting: 'waiting on approval',
};

function governanceSummaryOf(preview: PendingActionPreview): string {
  return String(preview.summary || preview.subject || 'a team or role change');
}

function buildGovernanceCardHtml(
  actionId: string,
  preview: PendingActionPreview,
  view: GovernanceCardView,
): string {
  const id = escapeHtml(actionId);
  const summary = escapeHtml(governanceSummaryOf(preview));
  const lines = Array.isArray(preview.lines) ? preview.lines : [];
  const askedBy = escapeHtml(String(preview.askedBy || ''));
  const approvers = Array.isArray(preview.approvers) ? preview.approvers.filter(Boolean) : [];
  // `data-kind` is what tells the click handler this is not a send, so its
  // button states read "approving…" instead of "sending…".
  const dataAttrs = `data-action-id="${id}" data-kind="rbac" data-subject="${summary}"`;
  const approveId = `${AUTHORIZE_ACTION_BUTTON_IDS.approve}-${actionId}`;
  const cancelId = `${AUTHORIZE_ACTION_BUTTON_IDS.cancel}-${actionId}`;

  const rows: string[] = [];
  if (view === 'approver' && askedBy) rows.push(renderMetaRow('asked by:', askedBy));
  rows.push(renderMetaRow('change:', summary));
  for (const line of lines.slice(0, 8)) {
    const text = String(line || '').trim();
    if (!text) continue;
    const at = text.indexOf(':');
    if (at > 0 && at < 28) {
      rows.push(renderMetaRow(`${text.slice(0, at)}:`, escapeHtml(text.slice(at + 1).trim())));
    } else {
      rows.push(`<div>${escapeHtml(text)}</div>`);
    }
  }

  const note = view === 'waiting'
    ? approvers.length > 0
      ? `asked ${escapeHtml(approvers.join(' and '))}. nothing changes until one of them approves — you'll hear back here.`
      : "nothing changes until someone who can approve it does — you'll hear back here."
    : view === 'approver'
      ? 'nothing changes until you decide, and the change is made under your access — not theirs.'
      : 'nothing changes until you tap — and you will get a receipt here either way.';

  const actions = view === 'waiting'
    ? renderPillButton({ id: cancelId, label: 'withdraw', variant: 'ghost', size: 'md', dataAttrs })
    : `${renderPillButton({
        id: approveId,
        label: view === 'approver' ? 'approve' : 'make the change',
        variant: 'primary',
        size: 'md',
        dataAttrs,
      })}
    ${renderPillButton({
      id: cancelId,
      label: view === 'approver' ? 'decline' : "don't",
      variant: 'ghost',
      size: 'md',
      dataAttrs,
    })}`;

  return renderCardShell({
    maxWidth: 560,
    title: GOVERNANCE_TITLES[view],
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>
  <div class="p0ac-note">${note}</div>
  <div class="p0ac-actions">
    ${actions}
  </div>`,
  });
}

/**
 * The offer card for ANY held action. A send keeps its exact previous html;
 * a team/role change renders the governance card in the right view.
 */
export function buildAuthorizeCardHtml(
  actionId: string,
  preview: PendingActionPreview,
  opts?: { view?: GovernanceCardView | null },
): string {
  if (isGovernancePreview(preview)) {
    return buildGovernanceCardHtml(actionId, preview, governanceCardView(preview, opts?.view));
  }
  return buildEmailAuthorizeCardHtml(actionId, preview);
}

/** PURE. The three governance outcome kinds, as the receipt reads them. */
export function isGovernanceOutcomeKind(kind: string | null | undefined): boolean {
  const k = String(kind || '');
  return k === 'rbac-done' || k === 'rbac-refused' || k === 'rbac-failed';
}

function buildGovernanceReceiptCardHtml(outcome: PendingActionOutcome): string {
  const done = outcome.kind === 'rbac-done';
  const declined = outcome.decision === 'cancel';
  const ink = done ? OK_INK : WARN_INK;
  const title = done
    ? 'done — the change was made'
    : declined
      ? 'not changed — declined'
      : outcome.kind === 'rbac-refused'
        ? 'not changed — the platform refused it'
        : outcome.kind === 'rbac-failed'
          ? 'not changed — a problem on our side, not yours'
          : outcome.kind === 'permission-denied'
            ? 'not changed — this needs access you do not have'
            : outcome.kind === 'expired'
              ? 'not changed — this card expired before it was decided'
              : outcome.kind === 'already-decided'
                ? 'not changed here — this card was already answered'
                : outcome.kind === 'superseded'
                  ? 'not changed — a newer request replaced this one'
                  : outcome.kind === 'unreachable'
                    ? 'not confirmed — the tap never reached us'
                    : 'not changed';
  const summary = escapeHtml(String(outcome.subject || 'the change'));
  const clock = receiptClock(outcome.decidedAt);
  const rows: string[] = [renderMetaRow('change:', summary)];
  if (clock) rows.push(renderMetaRow(done ? 'made at:' : 'at:', escapeHtml(clock)));

  const detail: string[] = [];
  // "why:" is the platform's OWN code-authored verdict for a refusal. Our
  // internal failures withhold it server-side, exactly as a send does.
  if (!done && outcome.reason) detail.push(renderMetaRow('why:', escapeHtml(String(outcome.reason))));
  if (!done && outcome.remediation) {
    detail.push(`<div style="margin-top:6px;">${renderMetaRow('next:', escapeHtml(String(outcome.remediation)))}</div>`);
  }

  return renderCardShell({
    maxWidth: 560,
    title,
    titleInk: ink,
    titleIconSvg: done ? renderStateIcon('check') : renderStateIcon('alert'),
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>${detail.length > 0 ? `\n  ${renderDetailBlock(detail)}` : ''}`,
  });
}

/**
 * The receipt for ANY decided held action. A send keeps its exact previous
 * html; a decided team/role change gets the governance receipt — buttonless
 * always, because a governance hold never re-arms (`retryable:false`): the
 * change either happened or the request is over.
 */
export function buildSendReceiptCardHtml(outcome: PendingActionOutcome): string {
  if (isGovernanceOutcomeKind(outcome.kind)) {
    return buildGovernanceReceiptCardHtml(outcome);
  }
  return buildEmailSendReceiptCardHtml(outcome);
}
